import { useState, useEffect, useCallback, useMemo } from "react";
import { Mail, Send, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const parseEmails = (txt) => txt.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
const RESULT_LABELS = [
  ["sent", "Inviti inviati", "text-emerald-700"],
  ["registered", "Già registrati", "text-slate-600"],
  ["recent", "Già invitati negli ultimi 7 giorni", "text-amber-700"],
  ["invalid", "Indirizzi non validi", "text-red-600"],
  ["failed", "Invio non riuscito", "text-red-600"],
];

function InviteResult({ result }) {
  const rows = useMemo(() => RESULT_LABELS.filter(([k]) => result[k].length > 0), [result]);
  return (
    <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="invite-result">
      {rows.map(([k, label, cls]) => (
        <p key={k} className={cls} data-testid={`invite-result-${k}`}>
          <span className="font-700">{label} ({result[k].length}):</span> {result[k].join(", ")}
        </p>
      ))}
    </div>
  );
}

function InviteHistory({ items }) {
  if (items.length === 0) return <p className="text-xs text-slate-400" data-testid="invite-history-empty">Nessun invito inviato finora.</p>;
  return (
    <div className="max-h-52 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200" data-testid="invite-history">
      {items.map((i) => (
        <div key={i.email} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
          <div className="min-w-0">
            <p className="truncate font-600 text-slate-800">{i.email}</p>
            <p className="text-slate-400">Invitato il {new Date(i.invited_at).toLocaleDateString("it-IT")}{i.count > 1 ? ` · ${i.count} inviti` : ""}</p>
          </div>
          {i.status === "registered" ? (
            <Badge className="shrink-0 gap-1 bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Registrato</Badge>
          ) : (
            <Badge className="shrink-0 gap-1 bg-sky-100 text-blue-700"><Clock className="h-3 w-3" /> Invitato</Badge>
          )}
        </div>
      ))}
    </div>
  );
}

export function InviteMediciDialog({ open, onClose }) {
  const [text, setText] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const emails = useMemo(() => parseEmails(text), [text]);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/admin/invites");
    setHistory(data.invites);
  }, []);
  useEffect(() => { if (open) { setResult(null); loadHistory(); } }, [open, loadHistory]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { emails, message });
      setResult(data);
      if (data.sent.length) { toast.success(`${data.sent.length} inviti inviati`); setText(""); }
      loadHistory();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Mail className="h-5 w-5 text-blue-700" /> Invita medici</DialogTitle>
          <DialogDescription>Ogni medico riceverà un'email con il pulsante "Registrati come Medico" e l'email già compilata.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="invite-emails">Indirizzi email</Label>
            <Textarea id="invite-emails" rows={4} value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5"
              placeholder={"dott.rossi@esempio.it, dott.ssa.bianchi@esempio.it\noppure uno per riga"} data-testid="invite-emails-input" />
            <p className="mt-1 text-xs text-slate-500" data-testid="invite-count">{emails.length} indirizzi · separali con virgola, spazio o a capo</p>
          </div>
          <div>
            <Label htmlFor="invite-message">Messaggio personale (facoltativo)</Label>
            <Textarea id="invite-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5"
              placeholder="Es. Su indicazione dell'Ordine dei Medici di Como..." data-testid="invite-message-input" />
          </div>
          <Button className="w-full gap-2 bg-blue-700 hover:bg-blue-800" disabled={busy || emails.length === 0} onClick={send} data-testid="invite-send-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia {emails.length > 0 ? `${emails.length} inviti` : "inviti"}
          </Button>
          {result && <InviteResult result={result} />}
          <div>
            <Label className="mb-1.5 block">Storico inviti</Label>
            <InviteHistory items={history} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
